import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { decodeHtmlEntities } from 'src/app/shared/utils/html.utils';
import { SKIP_HTML_DECODE } from './html-decode.context';

/**
 * A string that is itself serialized JSON (a stored schema/config blob, e.g. an assessment
 * template's `section.schema`) must NOT be entity-decoded: decoding entities inside it
 * (e.g. `&quot;` -> `"`) corrupts the JSON and breaks the consumer's `JSON.parse`. Gated on
 * cheap bracket checks (first + last char) before attempting a parse, so ordinary display text
 * is unaffected. Only reached for strings that contain an entity (see `decodeDeep`), so the
 * `JSON.parse` never runs on the overwhelming majority of response fields.
 */
function isSerializedJson(value: string): boolean {
    const trimmed = value.trim();
    const start = trimmed[0];
    const end = trimmed[trimmed.length - 1];
    if (!((start === '{' && end === '}') || (start === '[' && end === ']'))) return false;
    try {
        JSON.parse(trimmed);
        return true;
    } catch {
        return false;
    }
}

/**
 * Recursively decode HTML entities in every string of a JSON value, returning a new copy (never mutates the input).
 *
 * Intentionally NOT `html.utils#decodeHtmlDeep`: that helper is shallow (string | array-of-strings only, no object
 * recursion), uses the weaker `decodeHtml`, and has no serialized-JSON guard — it is tuned for the Mira "approve"
 * step, not for walking an arbitrary API response tree. Keeping them separate avoids changing that behaviour.
 */
function decodeDeep(value: unknown): unknown {
    if (typeof value === 'string') {
        // No `&` => no entity to decode => skip both the decode and the (costly) serialized-JSON parse.
        if (!value.includes('&')) return value;
        return isSerializedJson(value) ? value : decodeHtmlEntities(value);
    }
    if (Array.isArray(value)) return value.map(item => decodeDeep(item));
    if (value !== null && typeof value === 'object') {
        const out: Record<string, unknown> = {};
        for (const key of Object.keys(value)) {
            out[key] = decodeDeep((value as Record<string, unknown>)[key]);
        }
        return out;
    }
    return value;
}

/**
 * Decodes HTML entities in internal-API JSON response bodies so plain-text `{{ }}` rendering
 * shows `DPIA - GDPR & AI` instead of `DPIA - GDPR &amp; AI`. Skippable per-request
 * via SKIP_HTML_DECODE. See spec
 * docs/superpowers/specs/2026-08-11-html-entity-decode-interceptor-design.md.
 */
// No `providedIn` — registered manually via HTTP_INTERCEPTORS in app.module.ts and
// collaboration.module.ts; DI is entirely explicit, not a root singleton.
@Injectable()
export class HtmlDecodeInterceptor implements HttpInterceptor {
    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(req).pipe(map(event => this.decodeEvent(req, event)));
    }

    private decodeEvent(req: HttpRequest<unknown>, event: HttpEvent<unknown>): HttpEvent<unknown> {
        if (!(event instanceof HttpResponse) || !this.shouldDecode(req, event)) {
            return event;
        }
        return event.clone({ body: decodeDeep(event.body) });
    }

    private shouldDecode(req: HttpRequest<unknown>, res: HttpResponse<unknown>): boolean {
        if (req.context.get(SKIP_HTML_DECODE)) return false;
        if (!req.url.startsWith(environment.api.accessPointUrl)) return false;
        const body = res.body;
        return body !== null
            && typeof body === 'object'
            && !(body instanceof Blob)
            && !(body instanceof ArrayBuffer);
    }
}
